<template>
  <main
    class="campaign-area"
    :dir="uiLocale === 'en' ? 'ltr' : 'rtl'"
    :style="{ textAlign: uiLocale === 'en' ? 'left' : 'right' }"
  >
    <router-link
      class="campaign-button"
      :to="{ name: 'content-campaign-list' }"
    >
      {{ words.backToList }}
    </router-link>
    <header class="campaign-header">
      <div><h1>{{ id ? words.edit : words.create }}</h1><p>{{ words.intro }}</p></div><span class="campaign-state">{{ words[record.effectiveState || record.status || 'draft'] }}</span>
      <router-link
        v-if="id && $can('report', 'contentCampaign')"
        class="campaign-button"
        :to="{ name: 'content-campaign-reports', query: { campaignId: id } }"
      >
        {{ words.viewStats }}
      </router-link>
    </header>
    <p
      v-if="message && !feedbackAtFooter"
      ref="operationFeedback"
      tabindex="-1"
      class="campaign-message"
      role="alert"
    >
      {{ message }}
    </p><section
      v-if="writeUnknown"
      ref="unknownFeedback"
      tabindex="-1"
      class="campaign-message"
      role="status"
    >
      <p>{{ words.writeUnconfirmed }}</p>
      <button
        v-if="creationIntent"
        type="button"
        :disabled="busy || !!savedSnapshot"
        @click="reconcileCreation"
      >
        {{ words.reconcileCreation }}
      </button>
      <button
        v-if="id && unknownAction !== 'duplicate'"
        type="button"
        :disabled="busy"
        @click="checkSaved"
      >
        {{ words.checkSaved }}
      </button>
      <template v-if="savedSnapshot">
        <p>{{ words.savedState }}: {{ words[savedSnapshot.status] }} · {{ words.revision }} {{ savedSnapshot.revision }}</p><button
          type="button"
          @click="useSaved"
        >
          {{ words.useSaved }}
        </button>
      </template>
    </section><button
      v-if="conflict"
      type="button"
      @click="reloadConfirmed"
    >
      {{ words.reload }}
    </button><button
      v-if="loadFailed"
      type="button"
      :disabled="loading"
      @click="load"
    >
      {{ words.retry }}
    </button><p
      v-if="loading"
      role="status"
    >
      {{ words.loading }}
    </p><p
      v-if="busy && !feedbackAtFooter"
      role="status"
      aria-live="polite"
    >
      {{ words.processing }}
    </p><p v-if="record.status === 'published'">
      {{ words.publishedEdit }}
    </p>
    <div class="campaign-editor-layout">
      <form
        :aria-busy="busy"
        @submit.prevent="save"
        @invalid.capture.prevent="revealInvalid"
      >
        <section class="campaign-panel"><h2>{{ words.detailsStep }}</h2><fieldset :disabled="readOnly">
          <label>{{ words.name }}<input
            v-model="record.internalName"
            data-campaign-field="name"
            maxlength="120"
            required
          ></label>
          <label class="check"><input
            type="checkbox"
            :checked="allLanguages"
            @change="toggleLanguages($event.target.checked)"
          >{{ words.allLanguages }}</label><small>{{ words.languagesHint }}</small><div
            class="campaign-actions"
            :aria-label="words.language"
          >
            <label
              v-for="code in locales"
              :key="`enable-${code}`"
              class="check"
            ><input
              v-model="record.locales[code].enabled"
              type="checkbox"
            >{{ languageNames[code] }}</label>
          </div>
        </fieldset><div
          class="campaign-tabs"
          :aria-label="words.language"
        >
          <button
            v-for="code in locales"
            :key="code"
            type="button"
            :aria-pressed="locale === code"
            @click="locale = code"
          >
            {{ languageNames[code] }}
          </button>
        </div>
          <fieldset :disabled="readOnly"><button
                                           v-if="enabledLanguageCount > 1"
                                           type="button"
                                           @click="copyEmptyLanguages"
                                         >
                                           {{ words.copyEmptyLanguages }}
                                         </button><small v-if="enabledLanguageCount > 1">{{ words.copyEmptyHint }}</small>
            <div>
              <label>{{ words.label }}<input
                v-model="current.label"
                :dir="contentDir"
                style="text-align: start"
                maxlength="50"
                :aria-describedby="'campaign-message-label-hint'"
              ></label><small id="campaign-message-label-hint">{{ words.labelHint }}</small>
              <label>{{ words.heading }}<input
                v-model="current.title"
                data-campaign-field="creative"
                :dir="contentDir"
                style="text-align: start"
                maxlength="100"
              ></label><label>{{ words.body }}<textarea
                v-model="current.body"
                :dir="contentDir"
                style="text-align: start"
                maxlength="300"
              /></label>
              <div class="campaign-grid">
                <label>{{ words.template }}<select v-model="current.template"><option value="image-banner">{{ words.image }}</option><option value="text-card">{{ words.text }}</option><option value="compact-strip">{{ words.compact }}</option></select></label><label>{{ words.theme }}<select v-model="current.theme"><option value="soft-blue">{{ words.blue }}</option><option value="soft-rose">{{ words.rose }}</option><option value="neutral">{{ words.neutral }}</option></select></label>
              </div>
              <div
                v-if="current.template === 'image-banner'"
                class="campaign-upload"
              ><p>{{ words.uploadHint }}</p><p
                v-if="!mediaCapabilities.enabled"
                role="status"
              >
                {{ !mediaCapabilitiesLoaded ? words.loading : (mediaCapabilityError ? words[mediaCapabilityError] : words.uploadDisabled) }}
              </p><button
                v-if="mediaCapabilityError"
                type="button"
                :disabled="!mediaCapabilitiesLoaded"
                @click="loadMediaCapabilities"
              >{{ words.uploadCheckAgain }}</button><p v-if="mediaCapabilities.enabled && !marketingConfirmed">
                {{ words.uploadConfirmFirst }}
              </p><label class="check"><input
                v-model="marketingConfirmed"
                type="checkbox"
              >{{ words.marketingConfirm }}</label>
                <div class="campaign-grid">
                  <div
                    v-for="slot in imageSlots"
                    :key="slot.field"
                  >
                    <label>{{ words[slot.label] }}<input
                      type="file"
                      :accept="mediaCapabilities.mimeTypes.join(',')"
                      :disabled="!marketingConfirmed || !mediaCapabilities.enabled || uploadBusy(slot.field)"
                      @change="uploadImage($event.target.files[0], slot.field); $event.target.value = ''"
                    ></label><p
                      v-if="uploadBusy(slot.field)"
                      role="status"
                    >
                      {{ words.uploading }}
                    </p><p
                      v-if="uploadMessages[`${locale}:${slot.field}`]"
                      role="alert"
                    >
                      {{ uploadMessages[`${locale}:${slot.field}`] }}
                    </p><div v-if="current[slot.field]">
                      <template v-if="asset(current[slot.field])">
                        <img
                          class="campaign-upload-thumbnail"
                          :src="ownedPreviewUrl(current[slot.field]) || asset(current[slot.field]).url"
                          :alt="current.imageAlt"
                        ><small>{{ asset(current[slot.field]).name }}</small><small v-if="previewStates[current[slot.field]] === 'failed'">{{ words.imagePreviewUnavailable }}</small>
                      </template><small v-else>{{ words.imageUnavailable }}</small><button
                        type="button"
                        @click="removeImage(slot.field)"
                      >
                        {{ words.removeImage }}
                      </button>
                    </div>
                  </div>
                </div><button
                  v-if="enabledLanguageCount > 1"
                  type="button"
                  :disabled="!current.imageFileId || anyUpload"
                  @click="shareImages"
                >
                  {{ words.shareImages }}
                </button><small v-if="enabledLanguageCount > 1">{{ words.shareImagesHint }}</small><label>{{ words.alt }}<input
                  v-model="current.imageAlt"
                  :dir="contentDir"
                  style="text-align: start"
                  maxlength="160"
                ></label>
              </div>
              <h3>{{ words.action }}</h3><div class="campaign-grid">
                <label>{{ words.actionLabel }}<input
                  v-model="current.primaryAction.label"
                  :dir="contentDir"
                  style="text-align: start"
                  maxlength="60"
                ></label><label>{{ words.url }}<input
                  v-model="current.primaryAction.url"
                  :aria-invalid="destinationError(current.primaryAction) ? 'true' : 'false'"
                  aria-describedby="campaign-primary-url-hint"
                  maxlength="2048"
                  dir="ltr"
                  style="text-align: left"
                ></label>
              </div><small
                id="campaign-primary-url-hint"
                aria-live="polite"
              >{{ destinationError(current.primaryAction) || (current.primaryAction.kind === 'internal-link' ? words.internalUrlHint : words.externalUrlHint) }}</small><label>{{ words.destination }}<select v-model="current.primaryAction.kind"><option value="internal-link">{{ words.internal }}</option><option value="external-link">{{ words.external }}</option></select></label>
              <details><summary>{{ words.advanced }} <span
                v-if="messageExtras"
                class="campaign-state"
              >{{ words.activeExtra }}</span></summary><label>{{ words.description }}<textarea
                v-model="record.description"
                maxlength="1000"
              /></label><label class="check"><input
                v-model="current.primaryAction.openInNewTab"
                type="checkbox"
              >{{ words.newTab }}</label><label class="check"><input
                type="checkbox"
                :checked="!!current.secondaryAction"
                @change="secondary($event.target.checked)"
              >{{ words.secondary }}</label><div v-if="current.secondaryAction">
                <label>{{ words.actionLabel }}<input
                  v-model="current.secondaryAction.label"
                  :dir="contentDir"
                  style="text-align: start"
                  maxlength="60"
                ></label><label>{{ words.url }}<input
                  v-model="current.secondaryAction.url"
                  :aria-invalid="destinationError(current.secondaryAction) ? 'true' : 'false'"
                  aria-describedby="campaign-secondary-url-hint"
                  maxlength="2048"
                  dir="ltr"
                  style="text-align: left"
                ></label><small
                  id="campaign-secondary-url-hint"
                  aria-live="polite"
                >{{ destinationError(current.secondaryAction) || (current.secondaryAction.kind === 'internal-link' ? words.internalUrlHint : words.externalUrlHint) }}</small><label>{{ words.destination }}<select v-model="current.secondaryAction.kind"><option value="internal-link">{{ words.internal }}</option><option value="external-link">{{ words.external }}</option></select></label><label class="check"><input
                  v-model="current.secondaryAction.openInNewTab"
                  type="checkbox"
                >{{ words.newTab }}</label>
              </div></details>
            </div>
            <div
              v-for="code in incompleteLanguages"
              :key="`incomplete-${code}`"
              class="campaign-language-note"
            >
              <button
                type="button"
                @click="locale = code"
              >
                {{ languageNames[code] }}
              </button><span>{{ !record.locales[code].label.trim() ? words.missingLabel : words.incompleteLanguage }}</span>
            </div>
          </fieldset></section>
        <section class="campaign-panel"><h2>{{ words.audienceStep }}</h2><fieldset :disabled="readOnly"><div class="campaign-actions">
                                                                                                          <label
                                                                                                            v-for="type in ['article', 'video', 'article-category']"
                                                                                                            :key="type"
                                                                                                            class="check"
                                                                                                          ><input
                                                                                                            v-model="record.rules.contentTypes"
                                                                                                            data-campaign-field="contentType"
                                                                                                            type="checkbox"
                                                                                                            :value="type"
                                                                                                          >{{ words[type] }}</label>
                                                                                                        </div><label class="check"><input
                                                                                                          v-model="record.rules.globalScope"
                                                                                                          type="checkbox"
                                                                                                        >{{ words.globalFallback }}</label><small v-if="record.rules.globalScope">{{ words.globalHint }}</small><p
            v-if="positiveCount"
            class="campaign-message"
          >
            {{ words.positiveSummary }}: {{ positiveCount }}<small v-if="record.rules.globalScope">{{ words.globalFiltered }}</small>
          </p><RulePicker
            v-model="record.rules.contentIds"
            kind="content"
            :title="words.contentIds"
            :locale="locale"
            :maximum="100"
          /><RulePicker
            v-model="record.rules.tagIds"
            kind="tags"
            :title="words.tagIds"
            :locale="locale"
          />
          <p class="campaign-info">
            {{ words.tagMatchingHint }}
          </p>
          <label>{{ words.position }}<select v-model="record.placement.mode"><option value="automatic">{{ words.automatic }}</option><option value="end">{{ words.endPosition }}</option></select></label><h3>{{ words.devices }}</h3><div class="campaign-actions">
            <label
              v-for="device in ['desktop', 'mobile']"
              :key="device"
              class="check"
            ><input
              v-model="record.devices"
              data-campaign-field="device"
              type="checkbox"
              :value="device"
            >{{ device === 'desktop' ? words.desktopScope : words.mobileScope }}</label>
          </div>
          <details><summary>{{ words.advanced }} <span
            v-if="audienceExtras"
            class="campaign-state"
          >{{ words.activeExtra }} {{ audienceExtras }}</span></summary><p>{{ words.ruleIntro }}</p><label>{{ words.combineRules }}<select v-model="record.rules.matchMode"><option value="all">{{ words.allGroups }}</option><option value="any">{{ words.anyGroups }}</option></select></label><div
            v-for="group in groups"
            :key="group.field"
          >
            <RulePicker
              v-if="group.field === 'categoryIds'"
              v-model="record.rules[group.field]"
              :kind="group.kind"
              :title="words[group.field]"
              :locale="locale"
              :maximum="group.field === 'contentIds' ? 100 : 50"
            /><label v-if="group.mode">{{ words[group.field] }} · {{ words.groupMode }}<select v-model="record.rules[group.mode]"><option value="any">{{ words.any }}</option><option value="all">{{ words.every }}</option></select></label>
          </div><label>{{ words.keywords }}<textarea
            :value="record.rules.keywords.join('\n')"
            maxlength="1640"
            :placeholder="words.phrases"
            @change="phrases('keywords', $event.target.value)"
          /></label><label>{{ words.keywordScope }}<select v-model="record.rules.keywordScope"><option value="title">{{ words.titleOnly }}</option><option value="body">{{ words.bodyOnly }}</option><option value="title-and-body">{{ words.both }}</option><option value="title-body-and-tags">{{ words.titleBodyTags }}</option></select></label><label>{{ words.keywords }}<select v-model="record.rules.keywordMode"><option value="any">{{ words.any }}</option><option value="all">{{ words.every }}</option></select></label><label class="check"><input
            v-model="record.rules.includeRelatedCategories"
            type="checkbox"
          >{{ words.related }}</label><h3>{{ words.exclusion }}</h3><RulePicker
            v-for="group in groups"
            :key="group.exclude"
            v-model="record.rules[group.exclude]"
            :kind="group.kind"
            :title="words[group.field]"
            :locale="locale"
            :maximum="group.field === 'contentIds' ? 100 : 50"
          /><label>{{ words.keywords }}<textarea
            :value="record.rules.negativeKeywords.join('\n')"
            maxlength="1640"
            :placeholder="words.phrases"
            @change="phrases('negativeKeywords', $event.target.value)"
          /></label><small>{{ words.doctorUnavailable }}</small></details>
        </fieldset></section>
        <section class="campaign-panel">
          <h2>{{ words.scheduleStep }}</h2><fieldset :disabled="readOnly">
            <div class="campaign-grid">
              <label>{{ words.startTehran }}<campaign-date-time
                :value="scheduleInputs.startsAt"
                :locale="uiLocale"
                :label="words.startTehran"
                :disabled="readOnly"
                field="schedule"
                @change="setTime('startsAt', $event)"
              /></label><label>{{ words.endTehran }}<campaign-date-time
                :value="scheduleInputs.endsAt"
                :locale="uiLocale"
                :label="words.endTehran"
                :disabled="readOnly"
                @change="setTime('endsAt', $event)"
              /></label>
            </div><small>{{ words.tehranHint }}</small><p
              v-if="scheduleMessage"
              role="alert"
            >
              {{ scheduleMessage }}
            </p><details><summary>{{ words.advanced }} <span
              v-if="record.priority !== 50"
              class="campaign-state"
            >{{ words.priority }} {{ record.priority }}</span></summary><label>{{ words.priority }}<input
              v-model.number="record.priority"
              type="number"
              min="0"
              max="100"
            ></label></details>
          </fieldset>
        </section>
        <div class="campaign-actions">
          <button
            v-if="$can('manage', 'contentCampaign') && record.status !== 'archived'"
            class="primary"
            :disabled="readOnly || anyUpload"
          >
            {{ busy ? words.saving : words.save }}
          </button><span v-if="record.revision">{{ words.revision }} {{ record.revision }}</span>
        </div>
      </form>
      <aside class="campaign-live-preview">
        <h2>{{ words.livePreview }}</h2><p>{{ words.livePreviewHint }}</p><label>{{ words.viewport }}<select v-model="viewport"><option value="desktop">{{ words.desktop }}</option><option value="mobile">{{ words.mobile }}</option></select></label><div
          class="campaign-context"
          :class="viewport"
          :dir="locale === 'en' ? 'ltr' : 'rtl'"
          :style="{ textAlign: locale === 'en' ? 'left' : 'right' }"
          @click.capture.prevent
        >
          <CampaignPreview
            :presentation="livePresentation"
            :preview-image-url="livePreviewImageUrl"
            :locale="locale"
          />
        </div>
      </aside>
    </div>
    <details class="campaign-panel">
      <summary>{{ words.savedPreview }}</summary><p>{{ words.savedPreviewHint }}</p>
      <label>{{ words.previewContent }}<select
        v-model="previewSurface"
        @change="contentId = ''; previewResult = null"
      >
        <option value="article">{{ words.article }} / {{ words.video }}</option>
        <option value="article-category">{{ words['article-category'] }}</option>
      </select></label><RulePicker
        :value="contentId ? [contentId] : []"
        :kind="previewSurface === 'article-category' ? 'categories' : 'content'"
        :title="words.previewContent"
        :preferred-ids="previewSurface === 'article-category' ? record.rules.categoryIds : record.rules.contentIds"
        :locale="locale"
        :maximum="1"
        @input="contentId = $event[0] || ''"
      /><p
        v-if="!id || dirty"
        role="status"
      >
        {{ words.previewSaveFirst }}
      </p><p
        v-else-if="!validContent"
        role="status"
      >
        {{ words.previewChooseFirst }}
      </p><button
        type="button"
        :disabled="busy || !id || !validContent || dirty"
        @click="preview"
      >
        {{ words.preview }}
      </button><template v-if="previewResult">
        <ul><li
          v-for="(reason, index) in previewResult.reasons"
          :key="index"
        >
          {{ reasonText(reason, uiLocale) }}
        </li></ul><div
          v-if="previewResult.presentation"
          class="campaign-context"
          :class="viewport"
          :dir="locale === 'en' ? 'ltr' : 'rtl'"
          :style="{ textAlign: locale === 'en' ? 'left' : 'right' }"
          @click.capture.prevent
        >
          <h3 v-if="previewResult.content">
            {{ previewResult.content.title }}
          </h3><p
            v-for="(paragraph, index) in previewContext.before"
            :key="`before-${index}`"
          >
            {{ paragraph }}
          </p><CampaignPreview
            :presentation="savedPresentation"
            :preview-image-url="livePreviewImageUrl"
            :locale="locale"
          /><p
            v-for="(paragraph, index) in previewContext.after"
            :key="`after-${index}`"
          >
            {{ paragraph }}
          </p>
        </div><p v-else>
          {{ words.noPreview }}
        </p>
      </template>
    </details>
    <section
      v-if="id"
      class="campaign-panel"
    >
      <p
        v-if="feedbackAtFooter && (busy || message)"
        ref="operationFeedback"
        tabindex="-1"
        class="campaign-message"
        role="status"
        aria-live="polite"
      >
        {{ busy ? words.processing : message }}
      </p>
      <ul
        v-if="publishRequirements.length && ['draft', 'paused'].includes(record.status)"
        id="campaign-publish-requirements"
        class="campaign-publish-requirements"
        aria-live="polite"
      >
        <li
          v-for="(requirement, index) in publishRequirements"
          :key="index"
        >
          {{ requirement }}
        </li><li><button
          type="button"
          @click="reviewPublishRequirements"
        >
          {{ words.reviewRequired }}
        </button></li>
      </ul>
      <label
        v-if="$can('publish', 'contentCampaign') && record.status !== 'archived'"
        class="check"
      ><input
        v-model="confirmed"
        data-campaign-field="confirmation"
        type="checkbox"
      >{{ words.confirmPublish }}</label><div class="campaign-actions">
        <button
          v-if="$can('publish', 'contentCampaign') && record.status === 'draft'"
          class="primary"
          :disabled="publishRequirements.length > 0"
          aria-describedby="campaign-publish-requirements"
          @click="transition('publish')"
        >
          {{ busy ? words.processing : words.publish }}
        </button><button
          v-if="$can('publish', 'contentCampaign') && record.status === 'published'"
          :disabled="busy || dirty"
          @click="transition('pause')"
        >
          {{ words.pause }}
        </button><button
          v-if="$can('publish', 'contentCampaign') && record.status === 'paused'"
          :disabled="publishRequirements.length > 0"
          aria-describedby="campaign-publish-requirements"
          @click="transition('resume')"
        >
          {{ busy ? words.processing : words.resume }}
        </button><button
          v-if="$can('manage', 'contentCampaign')"
          :disabled="busy || dirty"
          @click="transition('duplicate')"
        >
          {{ words.duplicate }}
        </button><button
          v-if="$can('publish', 'contentCampaign') && record.status !== 'archived'"
          :disabled="busy || dirty"
          @click="archive"
        >
          {{ words.archive }}
        </button>
      </div><div
        v-if="archiveConfirmation"
        class="campaign-message"
        role="alert"
      >
        <p>{{ words.confirmArchive }}</p><button
          type="button"
          :disabled="busy"
          @click="transition('archive'); archiveConfirmation = false"
        >
          {{ words.archive }}
        </button><button
          type="button"
          @click="archiveConfirmation = false"
        >
          {{ words.cancel }}
        </button>
      </div>
    </section>
  </main>
</template>
<script>
import axios from '@/libs/axios'
import messages from './messages'
import reasonText from './reasons'
import { tehranInput, tehranUtc } from './time'
import CampaignDateTime from './CampaignDateTime.vue'
import {
  blankCampaign, creative, localeCodes, writePayload, errorKey,
} from './model'
import RulePicker from './RulePicker.vue'
import CampaignPreview from './CampaignPreview.vue'
import './campaign.css'

export default {
  components: { RulePicker, CampaignPreview, CampaignDateTime },
  beforeRouteUpdate(to, from, next) { next((!this.dirty && !this.busy && !this.writeUnknown) || window.confirm(this.words.unsavedLeave)) },
  beforeRouteLeave(to, from, next) { next((!this.dirty && !this.busy && !this.writeUnknown) || window.confirm(this.words.unsavedLeave)) },
  data() {
    return {
      record: blankCampaign(),
      mediaCapabilities: { enabled: false, maxBytes: 8388608, mimeTypes: ['image/jpeg', 'image/png'] },
      mediaCapabilitiesLoaded: false,
      mediaCapabilityError: null,
      capabilitySequence: 0,
      scheduleInputs: { startsAt: '', endsAt: '' },
      scheduleMessage: '',
      marketingConfirmed: false,
      assets: {},
      previewObjectUrls: {},
      previewReadTokens: {},
      previewReadEpoch: 0,
      previewStates: {},
      abilityUnsubscribe: null,
      uploadPending: {},
      uploadTokens: {},
      uploadMessages: {},
      uploadEpoch: 0,
      assetSequence: 0,
      imageSlots: [{ field: 'imageFileId', label: 'desktopImage' }, { field: 'mobileImageFileId', label: 'mobileImage' }],
      locale: 'fa',
      locales: localeCodes,
      languageNames: { fa: 'فارسی', en: 'English', ar: 'العربية' },
      loading: false,
      loadFailed: false,
      archiveConfirmation: false,
      busy: false,
      message: '',
      feedbackAtFooter: false,
      writeUnknown: false,
      unknownAction: '',
      creationIntent: null,
      savedSnapshot: null,
      conflict: false,
      confirmed: false,
      contentId: '',
      previewSurface: 'article',
      viewport: 'desktop',
      previewResult: null,
      nextReceipt: null,
      baseline: '',
      sequence: 0,
      groups: [{ field: 'contentIds', kind: 'content', exclude: 'excludeContentIds' }, {
        field: 'categoryIds', kind: 'categories', mode: 'categoryMode', exclude: 'excludeCategoryIds',
      }, {
        field: 'tagIds', kind: 'tags', mode: 'tagMode', exclude: 'excludeTagIds',
      }],
    }
  },
  computed: {
    uiLocale() { return this.$i18n.locale },
    contentDir() { return this.locale === 'en' ? 'ltr' : 'rtl' },
    words() { return messages[this.uiLocale] || messages.fa },
    id() { return this.$route.params.id || '' },
    current() { return this.record.locales[this.locale] },
    readOnly() { return this.loading || this.loadFailed || this.busy || this.writeUnknown || !this.$can('manage', 'contentCampaign') || this.record.status === 'archived' },
    dirty() { return JSON.stringify(writePayload(this.record)) !== this.baseline },
    enabledLanguageCount() { return localeCodes.filter(code => this.record.locales[code].enabled).length },
    publishRequirements() {
      const reasons = []
      if (this.busy || this.anyUpload || this.loading) reasons.push(this.words.processing)
      if (this.writeUnknown) reasons.push(this.words.writeUnconfirmed)
      if (this.loadFailed) reasons.push(this.words.failed)
      if (this.dirty) reasons.push(this.words.publishSaveFirst)
      if (!this.enabledLanguageCount) reasons.push(this.words.publishLanguage)
      this.incompleteLanguages.forEach(code => reasons.push(`${this.languageNames[code]}: ${this.words.publishFields}`))
      this.invalidDestinations.forEach(item => reasons.push(`${this.languageNames[item.code]}: ${item.message}`))
      if (!this.record.devices.length) reasons.push(this.words.selectDevice)
      if (!this.record.rules.contentTypes.length) reasons.push(this.words.selectContentType)
      if (this.scheduleMessage) reasons.push(this.scheduleMessage)
      if (!this.confirmed) reasons.push(this.words.publishConfirmFirst)
      return reasons
    },
    positiveCount() { return ['contentIds', 'tagIds', 'categoryIds', 'keywords'].reduce((sum, key) => sum + this.record.rules[key].length, 0) },
    savedPresentation() { if (!this.previewResult || !this.previewResult.presentation) return null; const value = this.previewResult.presentation; return this.viewport === 'mobile' && value.image && value.image.mobileUrl ? { ...value, image: { ...value.image, url: value.image.mobileUrl, mobileUrl: null } } : value },
    allLanguages() { return localeCodes.every(code => this.record.locales[code].enabled) },
    incompleteLanguages() { return localeCodes.filter(code => { const value = this.record.locales[code]; return value.enabled && (!value.label.trim() || !value.title.trim() || !value.primaryAction.label.trim() || !value.primaryAction.url.trim()) }) },
    invalidDestinations() { return localeCodes.flatMap(code => { const value = this.record.locales[code]; return value.enabled ? [value.primaryAction, value.secondaryAction].filter(Boolean).map(action => ({ code, message: this.destinationError(action) })).filter(item => item.message) : [] }) },
    anyUpload() { return Object.values(this.uploadPending).some(Boolean) },
    messageExtras() { return this.record.description || this.current.primaryAction.openInNewTab || this.current.secondaryAction },
    audienceExtras() { const { rules } = this.record; return ['categoryIds', 'keywords', 'excludeContentIds', 'excludeCategoryIds', 'excludeTagIds', 'negativeKeywords'].reduce((sum, key) => sum + (rules[key] || []).length, 0) + Number(rules.matchMode !== 'all' || rules.categoryMode !== 'any' || rules.tagMode !== 'any' || rules.keywordMode !== 'any' || rules.includeRelatedCategories) },
    livePreviewImageUrl() { const id = this.viewport === 'mobile' && this.current.mobileImageFileId ? this.current.mobileImageFileId : this.current.imageFileId; return this.ownedPreviewUrl(id) },
    livePresentation() {
      const desktop = this.asset(this.current.imageFileId); const mobile = this.asset(this.current.mobileImageFileId); const asset = this.viewport === 'mobile' && mobile ? mobile : desktop; return {
        ...this.current,
        campaignId: this.id || 'draft-preview',
        revision: this.record.revision || 1,
        title: this.current.title || this.words.heading,
        image: asset ? {
          url: asset.url, mobileUrl: this.viewport === 'desktop' && mobile ? mobile.url : null, width: asset.width, height: asset.height, alt: this.current.imageAlt,
        } : null,
        primaryAction: {
          ...this.current.primaryAction, label: this.current.primaryAction.label || this.words.actionLabel, url: '#', openInNewTab: false,
        },
        secondaryAction: this.current.secondaryAction ? { ...this.current.secondaryAction, url: '#', openInNewTab: false } : null,
      }
    },
    validContent() { return /^[a-f0-9]{24}$/i.test(this.contentId) },
    previewContext() {
      if (!this.previewResult || !this.previewResult.presentation || !this.previewResult.content || typeof DOMParser === 'undefined') return { before: [], after: [] }
      const doc = new DOMParser().parseFromString(this.previewResult.content.html || '', 'text/html'); doc.querySelectorAll('script,style,iframe,object,embed').forEach(node => node.remove())
      const paragraphs = Array.from(doc.body.children).filter(node => node.tagName === 'P').map(node => node.textContent.trim()).filter(text => text.length >= 40)
      let split = paragraphs.length; const total = paragraphs.reduce((sum, text) => sum + text.length, 0)
      if (this.previewResult.presentation.placement.mode === 'automatic' && paragraphs.length >= 4 && total >= 600) { let read = 0; for (let index = 0; index < paragraphs.length - 1; index += 1) { read += paragraphs[index].length; if (index >= 1 && read >= total / 3) { split = index + 1; break } } }
      return { before: paragraphs.slice(0, split), after: paragraphs.slice(split) }
    },
  },
  watch: {
    viewport() { this.previewResult = null; this.confirmed = false }, contentId() { this.previewResult = null; this.confirmed = false }, id() { this.load() }, locale() { this.previewResult = null; this.confirmed = false; this.uploadEpoch += 1; this.uploadPending = {}; this.uploadMessages = {}; this.clearPreviewUrls(); this.hydrateAssets() }, dirty(value) { if (value) { this.previewResult = null; this.confirmed = false } },
  },
  created() { this.load(); this.loadMediaCapabilities(); if (typeof window !== 'undefined' && window.addEventListener) { window.addEventListener('storage', this.accountPreviewChanged); window.addEventListener('beforeunload', this.warnBeforeUnload) } if (this.$ability && this.$ability.on) this.abilityUnsubscribe = this.$ability.on('updated', this.accountPreviewChanged) },
  beforeDestroy() { this.sequence += 1; this.uploadEpoch += 1; this.assetSequence += 1; this.capabilitySequence += 1; this.clearPreviewUrls(); if (typeof window !== 'undefined' && window.removeEventListener) { window.removeEventListener('storage', this.accountPreviewChanged); window.removeEventListener('beforeunload', this.warnBeforeUnload) } if (typeof this.abilityUnsubscribe === 'function') this.abilityUnsubscribe() },
  methods: {
    focusFeedback(sequence) {
      this.$nextTick(() => {
        if (sequence !== this.sequence) return
        const target = this.$refs.unknownFeedback || this.$refs.operationFeedback
        if (target) { target.focus({ preventScroll: true }); target.scrollIntoView({ block: 'center', behavior: 'auto' }) }
      })
    },
    warnBeforeUnload(event) { if (!this.dirty && !this.busy && !this.writeUnknown) return; event.preventDefault(); Object.assign(event, { returnValue: '' }) },
    revealInvalid(event) { const { target } = event; let ancestor = target.parentElement; while (ancestor) { if (ancestor.tagName === 'DETAILS') ancestor.open = true; ancestor = ancestor.parentElement } this.$nextTick(() => target.focus()) },
    reviewPublishRequirements() {
      if (this.writeUnknown) { this.focusFeedback(this.sequence); return }
      if (!this.enabledLanguageCount || this.incompleteLanguages.length) { this.locale = this.incompleteLanguages[0] || this.locale; this.$nextTick(() => this.focusField('creative')); return }
      if (this.invalidDestinations.length) { this.locale = this.invalidDestinations[0].code; this.$nextTick(() => this.focusField('creative')); return }
      if (!this.record.devices.length) { this.focusField('device'); return }
      if (!this.record.rules.contentTypes.length) { this.focusField('contentType'); return }
      if (this.scheduleMessage) { this.focusField('schedule'); return }
      this.focusField(this.dirty ? 'name' : 'confirmation')
    },
    focusField(name) { const target = this.$el && this.$el.querySelector(`[data-campaign-field="${name}"]`); if (target) this.revealInvalid({ target }) },
    reloadConfirmed() { if (!this.dirty || window.confirm(this.words.discardDraft)) this.load() },
    validSaved(record, expectedId, expectedLocales = []) {
      if (!record || !/^[a-f0-9]{24}$/i.test(record.id || '') || (expectedId && record.id !== expectedId) || !Number.isInteger(record.revision) || record.revision < 1 || !['draft', 'published', 'paused', 'archived'].includes(record.status)) return false
      if (typeof record.internalName !== 'string' || !Array.isArray(record.devices) || !record.devices.every(device => ['mobile', 'desktop'].includes(device)) || !record.rules || !Array.isArray(record.rules.contentTypes) || !record.placement || !['automatic', 'end'].includes(record.placement.mode)) return false
      if (!record.locales || Array.isArray(record.locales) || typeof record.locales !== 'object') return false
      if (expectedLocales.some(code => !Object.prototype.hasOwnProperty.call(record.locales, code))) return false
      if (['published', 'paused'].includes(record.status) && !Object.keys(record.locales).length) return false
      return Object.entries(record.locales).every(([code, value]) => localeCodes.includes(code) && value && typeof value.enabled === 'boolean' && ['label', 'title', 'body'].every(field => typeof value[field] === 'string') && (!value.primaryAction || ['label', 'url'].every(field => typeof value.primaryAction[field] === 'string')))
    },
    writeFailure(error, action) { const status = error && error.response && error.response.status; if (!status || status >= 500 || status === 408) { this.writeUnknown = true; this.unknownAction = action; this.savedSnapshot = null; this.message = '' } else this.failure(error) },
    prepareCreation(operation, url, payload) {
      if (!window.crypto || !window.crypto.getRandomValues) { this.message = this.words.requestUnavailable; return null }
      const bytes = window.crypto.getRandomValues(new Uint8Array(16)); bytes[6] = (bytes[6] % 16) + 64; bytes[8] = (bytes[8] % 64) + 128
      const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('')
      const requestId = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
      this.creationIntent = { operation, url, payload: JSON.parse(JSON.stringify({ ...payload, requestId })) }
      return this.creationIntent.payload
    },
    validCreationReceipt(record, intent) {
      const receipt = record && record.writeReceipt
      return !!intent && !!receipt && receipt.requestId === intent.payload.requestId && receipt.operation === intent.operation && typeof receipt.replayed === 'boolean'
    },
    async reconcileCreation() {
      if (this.busy || !this.writeUnknown || !this.creationIntent || this.savedSnapshot) return
      const intent = this.creationIntent; const { sequence } = this; this.busy = true; this.message = ''
      try {
        const response = await axios.post(intent.url, JSON.parse(JSON.stringify(intent.payload)), { timeout: 10000 })
        if (sequence !== this.sequence || intent !== this.creationIntent) return
        if (!this.validSaved(response.data) || !this.validCreationReceipt(response.data, intent)) throw new Error('INVALID_CREATION_RECEIPT')
        this.savedSnapshot = response.data; this.message = this.words.creationRecovered
      } catch (error) { if (sequence === this.sequence && intent === this.creationIntent) this.failure(error) } finally { if (sequence === this.sequence) { this.busy = false; this.focusFeedback(sequence) } }
    },
    async checkSaved() {
      if (this.busy || !this.id || this.unknownAction === 'duplicate') return
      this.busy = true; const { sequence, id } = this
      try { const response = await axios.get(`content-campaigns/${id}`, { timeout: 10000 }); if (sequence !== this.sequence) return; if (!this.validSaved(response.data, id)) throw new Error('INVALID_SAVED_CAMPAIGN'); this.savedSnapshot = response.data } catch (error) { if (sequence === this.sequence) this.failure(error) } finally { if (sequence === this.sequence) this.busy = false }
    },
    useSaved() { if (this.savedSnapshot && window.confirm(this.words.discardDraft)) { const saved = this.savedSnapshot; this.hydrate(saved); this.writeUnknown = false; this.savedSnapshot = null; this.unknownAction = ''; this.creationIntent = null; this.message = ''; if (saved.id !== this.id) this.$router.replace({ name: 'content-campaign-edit', params: { id: saved.id } }) } },

    ownedPreviewUrl(id) { return id && this.previewObjectUrls[id] ? this.previewObjectUrls[id] : '' },
    revokePreviewUrl(id) { if (!id) return; this.$set(this.previewReadTokens, id, (this.previewReadTokens[id] || 0) + 1); const url = this.previewObjectUrls[id]; if (url) { URL.revokeObjectURL(url); this.$delete(this.previewObjectUrls, id) } this.$delete(this.previewStates, id) },
    clearPreviewUrls() { this.previewReadEpoch += 1; Object.keys(this.previewObjectUrls).forEach(id => this.revokePreviewUrl(id)); this.previewStates = {} },
    accountPreviewChanged(event) { if (!event || !event.key || ['userData', 'accessToken', 'refreshToken'].includes(event.key)) { this.clearPreviewUrls(); this.assetSequence += 1; this.uploadEpoch += 1; this.uploadPending = {}; this.creationIntent = null; this.savedSnapshot = null; this.nextReceipt = null; this.sequence += 1; if (this.busy) { this.writeUnknown = true; this.unknownAction = 'session'; this.busy = false; this.message = this.words.sessionChanged } if (this.loading) { this.loading = false; this.loadFailed = true; this.conflict = true; this.message = this.words.sessionChanged } } },
    async loadPreviewBlob(id) {
      if (!/^[a-f0-9]{24}$/i.test(id || '') || this.ownedPreviewUrl(id)) return
      const epoch = this.previewReadEpoch; const { sequence } = this; const { locale } = this; const token = (this.previewReadTokens[id] || 0) + 1; this.$set(this.previewReadTokens, id, token); this.$set(this.previewStates, id, 'loading')
      try { const response = await axios.get(`content-campaigns/media/${id}/preview`, { responseType: 'blob', timeout: 10000 }); if (epoch !== this.previewReadEpoch || sequence !== this.sequence || locale !== this.locale || token !== this.previewReadTokens[id]) return; const blob = response.data; if (!(blob instanceof Blob) || !['image/jpeg', 'image/png'].includes(blob.type) || blob.size <= 0 || blob.size > 8388608) throw new Error('INVALID_PREVIEW_IMAGE'); const url = URL.createObjectURL(blob); this.$set(this.previewObjectUrls, id, url); this.$set(this.previewStates, id, 'ready') } catch (error) { if (epoch === this.previewReadEpoch && sequence === this.sequence && locale === this.locale && token === this.previewReadTokens[id]) this.$set(this.previewStates, id, 'failed') }
    },
    uploadError(error) { const status = error && error.response && error.response.status; if (status === 400 || status === 422) return this.words.uploadRejected; if (status === 413) return this.words.uploadTooLarge; if (status === 429) return this.words.uploadRateLimited; if (status === 401) return this.words.uploadSession; if (status === 403) return this.words.forbidden; if (status === 503) return this.words.uploadUnavailable; return this.words.uploadFailed },

    reasonText,
    localTime(value) { try { return new Date(value).toLocaleString(this.uiLocale, { timeZone: this.record.displayTimezone }) } catch (error) { return this.words.unknown } },
    hydrate(data) { const record = { ...blankCampaign(), ...data }; record.locales = { fa: creative(), en: creative(), ar: creative() }; localeCodes.forEach(code => { if (data.locales && data.locales[code]) record.locales[code] = { ...creative(), ...data.locales[code], primaryAction: { ...creative().primaryAction, ...data.locales[code].primaryAction } } }); this.record = record; this.baseline = JSON.stringify(writePayload(record)); this.previewResult = null; this.confirmed = false; this.conflict = false; this.loadFailed = false; this.scheduleInputs = { startsAt: tehranInput(record.startsAt), endsAt: tehranInput(record.endsAt) }; this.scheduleMessage = ''; this.hydrateAssets() },
    optional(object, key, value) { if (value.trim()) this.$set(object, key, value.trim()); else this.$delete(object, key) },
    secondary(enabled) {
      if (enabled) {
        this.$set(this.current, 'secondaryAction', {
          id: 'secondary', kind: 'internal-link', label: '', url: '', openInNewTab: false,
        })
      } else this.$delete(this.current, 'secondaryAction')
    },
    phrases(key, value) { this.record.rules[key] = [...new Set(value.split('\n').map(item => item.trim()).filter(Boolean))] },
    datetime(value) { return tehranInput(value) },
    setTime(key, value) { this.confirmed = false; this.previewResult = null; this.$set(this.scheduleInputs, key, value); this.scheduleMessage = ''; try { const iso = tehranUtc(value); if (iso) this.$set(this.record, key, iso); else this.$delete(this.record, key); this.record.displayTimezone = 'Asia/Tehran'; this.validateSchedule() } catch (error) { this.scheduleMessage = this.words.invalidDate } },
    validateSchedule() { try { const start = tehranUtc(this.scheduleInputs.startsAt); const end = tehranUtc(this.scheduleInputs.endsAt); if (start && end && end <= start) { this.scheduleMessage = this.words.invalidWindow; return false } this.scheduleMessage = ''; return true } catch (error) { this.scheduleMessage = this.words.invalidDate; return false } },
    toggleLanguages(enabled) { if (this.readOnly) return; localeCodes.forEach(code => { this.record.locales[code].enabled = enabled }) },
    hasCreativeContent(value) { return ['label', 'title', 'body', 'imageAlt', 'imageFileId', 'mobileImageFileId'].some(key => typeof value[key] === 'string' && value[key].trim()) || ['primaryAction', 'secondaryAction'].some(key => value[key] && (value[key].label.trim() || value[key].url.trim() || value[key].openInNewTab || value[key].kind !== 'internal-link')) || value.template !== 'text-card' || value.theme !== 'soft-blue' || value.imageEssential },
    copyEmptyLanguages() { if (this.readOnly) return; localeCodes.filter(code => code !== this.locale && this.record.locales[code].enabled).forEach(code => { const target = this.record.locales[code]; if (!this.hasCreativeContent(target)) this.$set(this.record.locales, code, { ...JSON.parse(JSON.stringify(this.current)), enabled: true }) }); this.message = this.words.copyDone },
    shareImages() { if (this.readOnly || !this.current.imageFileId || this.anyUpload) return; localeCodes.filter(code => code !== this.locale && this.record.locales[code].enabled).forEach(code => { ['imageFileId', 'mobileImageFileId'].forEach(field => { if (this.current[field]) this.$set(this.record.locales[code], field, this.current[field]); else this.$delete(this.record.locales[code], field) }) }); this.message = this.words.sharedImages },
    async loadMediaCapabilities() {
      this.capabilitySequence += 1
      const epoch = this.capabilitySequence
      this.mediaCapabilitiesLoaded = false
      this.mediaCapabilityError = null
      this.mediaCapabilities.enabled = false
      if (!this.$can('manage', 'contentCampaign')) { this.mediaCapabilityError = 'forbidden'; this.mediaCapabilitiesLoaded = true; return }
      try {
        const response = await axios.get('content-campaigns/media/capabilities', { timeout: 10000 })
        const value = response.data
        if (epoch !== this.capabilitySequence) return
        if (!value || typeof value.enabled !== 'boolean' || !Number.isInteger(value.maxBytes) || value.maxBytes <= 0 || value.maxBytes > 8388608 || !Array.isArray(value.mimeTypes)) throw new Error('INVALID_MEDIA_CAPABILITIES')
        const mimeTypes = value.mimeTypes.filter(type => ['image/jpeg', 'image/png'].includes(type))
        if (value.enabled && !mimeTypes.length) throw new Error('INVALID_MEDIA_CAPABILITIES')
        this.mediaCapabilities = { enabled: value.enabled, maxBytes: value.maxBytes, mimeTypes }
      } catch (error) {
        if (epoch === this.capabilitySequence) {
          const status = error && error.response && error.response.status
          this.mediaCapabilityError = 'uploadCheckFailed'
          if (status === 401) this.mediaCapabilityError = 'uploadSession'
          if (status === 403) this.mediaCapabilityError = 'forbidden'
        }
      } finally { if (epoch === this.capabilitySequence) this.mediaCapabilitiesLoaded = true }
    },
    asset(id) { return this.assets[id] || null },
    uploadBusy(field) { return this.uploadPending[`${this.locale}:${field}`] === true },
    removeImage(field) { if (this.readOnly) return; const key = `${this.locale}:${field}`; this.$set(this.uploadTokens, key, (this.uploadTokens[key] || 0) + 1); this.$set(this.uploadPending, key, false); this.revokePreviewUrl(this.current[field]); this.$delete(this.current, field) },
    async hydrateAssets() { this.assetSequence += 1; const sequence = this.assetSequence; const ids = [...new Set(localeCodes.flatMap(code => [this.record.locales[code].imageFileId, this.record.locales[code].mobileImageFileId]).filter(Boolean))]; if (!ids.length) return; try { const response = await axios.post('content-campaigns/media/options', { ids }, { timeout: 10000 }); if (sequence !== this.assetSequence) return; const items = response.data && response.data.items; if (!Array.isArray(items)) return; items.filter(item => ids.includes(item.id) && item.readiness === 'ready' && typeof item.url === 'string').forEach(item => this.$set(this.assets, item.id, item)); [this.current.imageFileId, this.current.mobileImageFileId].filter(id => id && this.assets[id]).forEach(id => this.loadPreviewBlob(id)) } catch (error) { if (sequence === this.assetSequence) this.message = this.words.imageUnavailable } },
    async uploadImage(file, field) {
      if (!file || this.readOnly || !this.mediaCapabilities.enabled || !this.marketingConfirmed || !this.imageSlots.some(slot => slot.field === field)) return
      const { locale } = this; const key = `${locale}:${field}`; this.$set(this.uploadMessages, key, '')
      if (!this.mediaCapabilities.mimeTypes.includes(file.type) || file.size > this.mediaCapabilities.maxBytes || file.size <= 0) { this.$set(this.uploadMessages, key, this.words.uploadInvalid); return }
      if (this.uploadPending[key]) return
      const epoch = this.uploadEpoch; const { sequence } = this; const token = (this.uploadTokens[key] || 0) + 1; this.$set(this.uploadTokens, key, token); this.$set(this.uploadPending, key, true)
      try { const body = new FormData(); body.append('file', file); body.append('name', file.name.slice(0, 120)); body.append('marketingAuthorityConfirmed', 'true'); const response = await axios.post('content-campaigns/media', body, { timeout: 30000 }); if (epoch !== this.uploadEpoch || sequence !== this.sequence || locale !== this.locale || token !== this.uploadTokens[key]) return; const item = response.data; if (!item || !/^[a-f0-9]{24}$/i.test(item.id || '') || item.readiness !== 'ready' || typeof item.url !== 'string') throw new Error('INVALID_UPLOADED_MEDIA'); const previous = this.record.locales[locale][field]; if (previous && previous !== item.id) this.revokePreviewUrl(previous); this.$set(this.assets, item.id, item); this.$set(this.record.locales[locale], field, item.id); this.loadPreviewBlob(item.id) } catch (error) { if (epoch === this.uploadEpoch && sequence === this.sequence && locale === this.locale && token === this.uploadTokens[key]) this.$set(this.uploadMessages, key, this.uploadError(error)) } finally { if (epoch === this.uploadEpoch && token === this.uploadTokens[key]) this.$set(this.uploadPending, key, false) }
    },
    failure(error) { const key = errorKey(error); const code = error && error.response && error.response.data && error.response.data.message; this.message = typeof code === 'string' && code.startsWith('CAMPAIGN_') && key !== 'conflict' && key !== 'forbidden' ? reasonText(code, this.uiLocale) : this.words[key]; this.conflict = key === 'conflict' },
    async load() {
      this.feedbackAtFooter = false; this.sequence += 1; this.uploadEpoch += 1; this.assetSequence += 1; this.clearPreviewUrls(); this.uploadPending = {}; const { sequence } = this; this.loading = true; this.loadFailed = false; this.busy = false; this.message = ''; this.writeUnknown = false; this.savedSnapshot = null; this.unknownAction = ''; this.creationIntent = null
      if (!this.id) { const draft = blankCampaign(); const locale = localeCodes.includes(this.uiLocale) ? this.uiLocale : 'fa'; draft.locales[locale].enabled = true; this.locale = locale; this.hydrate(draft); this.loading = false; return }
      try { const response = await axios.get(`content-campaigns/${this.id}`, { timeout: 10000 }); if (sequence === this.sequence) { if (!this.validSaved(response.data, this.id)) throw new Error('INVALID_LOADED_CAMPAIGN'); this.hydrate(response.data); if (this.nextReceipt && this.nextReceipt.id === this.id) { this.message = this.words[this.nextReceipt.key]; this.nextReceipt = null; this.focusFeedback(sequence) } } } catch (error) { if (sequence === this.sequence) { this.failure(error); this.loadFailed = true } } finally { if (sequence === this.sequence) this.loading = false }
    },
    destinationError(action) {
      if (!action || !action.url) return ''
      const value = action.url
      const hint = action.kind === 'internal-link' ? this.words.internalUrlHint : this.words.externalUrlHint
      let decoded
      try { decoded = decodeURIComponent(value) } catch (_) { return hint }
      const unsafeCharacters = text => Array.from(text).some(character => character === '\\' || character.charCodeAt(0) <= 32 || character.charCodeAt(0) === 127)
      if (value.length > 2048 || unsafeCharacters(value) || unsafeCharacters(decoded) || decoded.startsWith('//') || /%[0-9a-f]{2}/i.test(decoded)) return hint
      if (action.kind === 'internal-link') {
        const about = !/%23/i.test(value) && /^\/(?:fa\/|en\/|ar\/)?page\/about-us\/?(?:#[a-zA-Z][a-zA-Z0-9_-]*)?$/.test(decoded)
        return about || /^\/(?:fa\/|en\/|ar\/)?(?:blog\/article|mag\/article|mag\/video|video|service|contact|counseling)(?:\/[\p{L}\p{N}_-]+)*\/?(?:#[a-zA-Z][a-zA-Z0-9_-]*)?$/u.test(decoded) ? '' : hint
      }
      if (action.kind !== 'external-link') return hint
      try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password && !url.port && !url.search && url.pathname.split('/').every(part => part !== '..' && part !== '.') ? '' : hint } catch (_) { return hint }
    },
    async save() {
      this.feedbackAtFooter = false
      if (this.busy || this.readOnly || this.anyUpload) return
      if (this.invalidDestinations.length) { this.message = this.invalidDestinations[0].message; this.locale = this.invalidDestinations[0].code; this.$nextTick(() => this.focusField('creative')); return }
      if (!this.validateSchedule()) { this.focusField('schedule'); return }
      if (!this.record.devices.length) { this.message = this.words.selectDevice; this.focusField('device'); return }
      if (!this.record.rules.contentTypes.length) { this.message = this.words.selectContentType; this.focusField('contentType'); return }
      this.busy = true; this.message = ''; const { id } = this; const { sequence } = this; let expectedStatus = 'draft'; if (id) expectedStatus = this.record.status === 'published' ? 'paused' : this.record.status
      try { let payload = writePayload(this.record); if (!id) { payload = this.prepareCreation('create', 'content-campaigns', payload); if (!payload) return } const response = id ? await axios.patch(`content-campaigns/${id}`, payload, { timeout: 10000 }) : await axios.post('content-campaigns', payload, { timeout: 10000 }); if (sequence !== this.sequence) return; if ((!id && !this.validCreationReceipt(response.data, this.creationIntent)) || !this.validSaved(response.data, id, Object.keys(payload.locales)) || (id && response.data.revision <= this.record.revision) || response.data.status !== expectedStatus) throw new Error('INVALID_SAVE_RESPONSE'); const paused = this.record.status === 'published' && response.data.status === 'paused'; this.hydrate(response.data); this.creationIntent = null; this.message = paused ? this.words.savedPaused : this.words.saved; if (!id) { this.nextReceipt = { id: response.data.id, key: 'saved' }; this.busy = false; this.$router.replace({ name: 'content-campaign-edit', params: { id: response.data.id } }) } } catch (error) { if (sequence === this.sequence) { this.writeFailure(error, id ? 'save' : 'create'); if (!this.writeUnknown) this.creationIntent = null } } finally { if (sequence === this.sequence) { this.busy = false; this.focusFeedback(sequence) } }
    },
    validPreview(value) {
      if (!value || !Array.isArray(value.reasons) || !value.reasons.every(reason => typeof reason === 'string') || !Object.prototype.hasOwnProperty.call(value, 'presentation')) return false
      if (value.content && (typeof value.content.title !== 'string' || typeof value.content.html !== 'string')) return false
      const card = value.presentation
      return card === null || (!!card && typeof card.title === 'string' && card.placement && ['automatic', 'end'].includes(card.placement.mode) && card.primaryAction && typeof card.primaryAction.label === 'string' && typeof card.primaryAction.url === 'string')
    },
    async preview() {
      this.feedbackAtFooter = false
      if (this.busy || this.loading || this.loadFailed || !this.validContent || this.dirty) return
      this.busy = true; const {
        sequence, locale, contentId, previewSurface,
      } = this; const displayDevice = this.viewport; this.message = ''
      try {
        const response = await axios.post('content-campaigns/preview', {
          campaignId: this.id, contentId, locale, displayDevice, contentSurface: previewSurface,
        }, { timeout: 10000 }); if (sequence === this.sequence && locale === this.locale && contentId === this.contentId && previewSurface === this.previewSurface && displayDevice === this.viewport) { if (!this.validPreview(response.data)) throw new Error('INVALID_PREVIEW_RESPONSE'); this.previewResult = response.data }
      } catch (error) { if (sequence === this.sequence) this.failure(error) } finally { if (sequence === this.sequence) this.busy = false }
    },
    archive() { this.archiveConfirmation = true },
    async transition(action) {
      if (this.writeUnknown || this.busy || this.anyUpload || this.loading || this.loadFailed || this.dirty || (['publish', 'resume'].includes(action) && this.publishRequirements.length) || (['publish', 'resume'].includes(action) && !this.validateSchedule()) || !this.$can(action === 'duplicate' ? 'manage' : 'publish', 'contentCampaign')) return
      this.feedbackAtFooter = true; this.busy = true; this.message = ''; const { sequence } = this
      try {
        const url = `content-campaigns/${this.id}/${action}`; let payload = { revision: this.record.revision, confirmGlobalScope: this.record.rules.globalScope && this.confirmed }; if (action === 'duplicate') { payload = this.prepareCreation('duplicate', url, payload); if (!payload) return } const response = await axios.post(url, payload, { timeout: 10000 }); if (sequence !== this.sequence) return; if ((action === 'duplicate' && !this.validCreationReceipt(response.data, this.creationIntent)) || !this.validSaved(response.data, action === 'duplicate' ? null : this.id) || (action !== 'duplicate' && response.data.revision <= this.record.revision) || (action === 'duplicate' && response.data.id === this.id) || response.data.status !== ({
          duplicate: 'draft', publish: 'published', resume: 'published', pause: 'paused', archive: 'archived',
        }[action])) throw new Error('INVALID_TRANSITION_RESPONSE'); if (action === 'duplicate') { this.creationIntent = null; this.nextReceipt = { id: response.data.id, key: 'duplicateCompleted' }; this.busy = false; this.$router.push({ name: 'content-campaign-edit', params: { id: response.data.id } }) } else { this.hydrate(response.data); this.message = this.words[`${action}Completed`] }
      } catch (error) { if (sequence === this.sequence) { this.writeFailure(error, action); if (!this.writeUnknown) this.creationIntent = null } } finally { if (sequence === this.sequence) { this.busy = false; this.focusFeedback(sequence) } }
    },
  },
}
</script>
